<template>
	<EChart v-if="chartReady" :option="chartOption"
		@chart-mouseover="onSeriesHover" @chart-mouseout="onSeriesOut" />
</template>

<script>
import EChart from "@/components/charts/EChart.vue";
import { parseToCompactMoney, parseToMoney } from "@/utils/currency";
import tooltipFocusMixin from "./tooltipFocusMixin";

// Chart genérico de los cards del tab KPIs: combina barras (arriba/abajo,
// apilables) y líneas sobre un eje X mensual compartido, con hasta DOS ejes Y
// derivados de las unidades de las series (money / count / percent). Cada
// card del tab declara sus series con `{ name, type, unit, data, down?,
// stack? }` y este componente resuelve ejes, colores, leyenda y tooltip —
// mismas convenciones visuales que SalesClientsActivityChart.
const SERIES_COLOR_ANCHORS = [
	"--report-chart-segment-1",
	"--report-chart-accent",
	"--report-chart-segment-2",
	"--report-chart-segment-3",
	"--report-chart-other",
];
const TEXT_MUTED_ANCHOR = "--report-text-muted";
const TEXT_ANCHOR = "--report-text";
const BORDER_ANCHOR = "--report-border";
const TOOLTIP_BG_ANCHOR = "--report-tooltip-bg";
const TOOLTIP_TEXT_ANCHOR = "--report-tooltip-text";

// Formatters por unidad. Percent en enteros (regla del repo: los % de los
// reportes se muestran sin decimales).
const AXIS_FORMATTERS = {
	money: (val) => parseToCompactMoney(val),
	count: (val) => Math.round(val),
	percent: (val) => `${Math.round(val)}%`,
};
const TOOLTIP_FORMATTERS = {
	money: (val) => parseToMoney(val),
	count: (val) => `${Math.round(val)}`,
	percent: (val) => `${Math.round(val)}%`,
};

export default {
	name: "KpiComposedChart",

	components: { EChart },

	mixins: [tooltipFocusMixin],

	props: {
		monthHeaders: { type: Array, required: true },
		// [{ name, type: 'bar'|'line', unit: 'money'|'count'|'percent',
		//    data: [Number|null], down?: Boolean, stack?: String }]
		// `down` invierte la serie (barra hacia abajo: salientes/pérdidas).
		series: { type: Array, required: true },
	},

	data() {
		return {
			chartReady: false,
			palette: [],
			textColor: "",
			textMutedColor: "",
			borderColor: "",
			tooltipBg: "",
			tooltipText: "",
		};
	},

	computed: {
		// Unidades en orden de aparición → índice de eje Y (máx 2; una tercera
		// unidad comparte el eje derecho antes que agregar un tercer eje ilegible).
		unitAxisIndex() {
			const units = [];
			this.series.forEach((s) => {
				if (!units.includes(s.unit)) units.push(s.unit);
			});
			const map = {};
			units.forEach((unit, i) => { map[unit] = Math.min(i, 1); });
			return map;
		},

		axisUnits() {
			const units = [];
			Object.entries(this.unitAxisIndex).forEach(([unit, idx]) => {
				if (!units[idx]) units[idx] = unit;
			});
			return units;
		},

		hasDownSeries() {
			return this.series.some((s) => s.down);
		},

		chartOption() {
			const yAxis = this.axisUnits.map((unit, idx) => ({
				type: "value",
				position: idx === 0 ? "left" : "right",
				...(unit === "count" ? { minInterval: 1 } : {}),
				axisLine: { show: false },
				axisTick: { show: false },
				// Sólo el eje izquierdo pinta la grid, como en el resto de los
				// charts del reporte.
				splitLine: idx === 0
					? { lineStyle: { color: this.borderColor, type: "dashed" } }
					: { show: false },
				axisLabel: {
					color: this.textMutedColor,
					fontSize: 11,
					formatter: AXIS_FORMATTERS[unit] || ((v) => v),
				},
			}));

			return {
				grid: { top: 44, right: 48, bottom: 28, left: 48, containLabel: false },
				legend: {
					show: true,
					left: 0,
					top: 0,
					itemGap: 14,
					itemWidth: 14,
					itemHeight: 8,
					textStyle: { color: this.textColor, fontSize: 11, fontWeight: 500 },
				},
				tooltip: {
					trigger: "axis",
					confine: true,
					extraCssText: "max-width:280px; white-space:normal; word-break:break-word;",
					backgroundColor: this.tooltipBg,
					borderColor: this.tooltipBg,
					textStyle: { color: this.tooltipText, fontSize: 12 },
					formatter: (params) => {
						if (!Array.isArray(params) || params.length === 0) return "";
						const rows = params.filter((p) => p.value != null).map((p) => {
							const spec = this.series[p.seriesIndex] || {};
							const format = TOOLTIP_FORMATTERS[spec.unit] || ((v) => v);
							// Las series `down` viajan negadas para el render;
							// el tooltip muestra la magnitud real con su signo.
							const value = format(p.value);
							return `
								<div style="display:flex;justify-content:space-between;gap:12px;align-items:baseline;${this.focusRowStyle(p.seriesName)}">
									<span style="min-width:0;">${p.marker}${p.seriesName}</span>
									<span style="flex-shrink:0;">${value}</span>
								</div>
							`;
						}).join("");
						return `
							<div style="font-weight:600;margin-bottom:6px;">${params[0].axisValueLabel}</div>
							${rows}
						`;
					},
				},
				xAxis: {
					type: "category",
					data: this.monthHeaders,
					axisLine: { lineStyle: { color: this.borderColor } },
					axisTick: { show: false },
					axisLabel: { color: this.textMutedColor, fontSize: 11, margin: 10 },
				},
				yAxis,
				series: this.series.map((spec, idx) => this.buildSeries(spec, idx)),
			};
		},
	},

	mounted() {
		this.resolveCssColors();
		this.chartReady = this.palette.length > 0;
	},

	methods: {
		buildSeries(spec, idx) {
			const color = this.palette[idx % this.palette.length];
			const data = (spec.data || []).map(
				(value) => (value == null ? null : (spec.down ? -value : value)),
			);
			const base = {
				name: spec.name,
				yAxisIndex: this.unitAxisIndex[spec.unit] || 0,
				data,
				emphasis: { focus: "series" },
			};
			if (spec.type === "line") {
				return {
					...base,
					type: "line",
					smooth: true,
					showSymbol: false,
					// Un null es "sin dato" (ej. sin mes previo para el MoM):
					// la línea se corta, no se interpola.
					connectNulls: false,
					lineStyle: { width: 2, color },
					itemStyle: { color },
				};
			}
			return {
				...base,
				type: "bar",
				stack: spec.stack,
				barMaxWidth: 28,
				itemStyle: { color, borderRadius: 0 },
			};
		},

		resolveCssColors() {
			if (typeof window === "undefined") return;
			// Tokens leídos del host dentro del [data-theme] del tab — mismo
			// criterio que los demás charts (documentElement ignoraría el tema).
			const host = this.$el && this.$el.nodeType === 1
				? this.$el
				: (this.$el && this.$el.parentElement) || document.documentElement;
			const root = getComputedStyle(host);
			this.palette = SERIES_COLOR_ANCHORS
				.map((anchor) => root.getPropertyValue(anchor).trim())
				.filter(Boolean);
			this.textColor = root.getPropertyValue(TEXT_ANCHOR).trim();
			this.textMutedColor = root.getPropertyValue(TEXT_MUTED_ANCHOR).trim();
			this.borderColor = root.getPropertyValue(BORDER_ANCHOR).trim();
			this.tooltipBg = root.getPropertyValue(TOOLTIP_BG_ANCHOR).trim();
			this.tooltipText = root.getPropertyValue(TOOLTIP_TEXT_ANCHOR).trim();
		},
	},
};
</script>

<style scoped lang="scss">
:deep(.echart) {
	width: 100%;
	height: 100%;
}
</style>
